import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  GridedContent,
  GridedMain,
  IconChevronLeft,
  IconInfoOutlined,
  InputAutoComplete,
  Typography,
  useLayout,
} from '@capitalise/ui-design-system';
import CompanySearch from 'RemoteBusiness/CompanySearch';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { makeSelectAddNewClientCompaniesList } from 'reducers/monitor/connect-wizzard/selectors';
import * as monitorActions from 'reducers/monitor/reducer';
import {
  SearchClientTitle,
  SearchClientHeaderAlpha,
  SearchClientFooter,
  ListCompanyItem,
} from '../../components';
import { useSearchClientContext } from '../../hooks/useSearchClientContext';
import { CompanyBusinessType } from '../../../../enums/company-type';
import {
  makeSelectUser,
  makeSelectUserCompanyId,
  makeSelectUserClientsLimit,
  makeSelectUserFeature,
} from '../../../../reducers/user/selectors';
import Entity from '../../../../models/Entity';
import { closeMonitorWizzard } from '../../../../reducers/monitor/reducer';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';
import ArchiveExistingClients from '../ArchiveExistingClients';
import { requestArchiveClient } from '../../../../reducers/monitor/client/actions';
import CreditLimitSidePanel from '../CreditLimitSidePanel';
import { CreditLimitProvider } from '../../hooks/useCreditLimitContext';
import { useSSELoadingMultipleEntities } from 'services/sse/queries';
import { events } from 'enums/sse';
import { meRequest } from '../../../../reducers/user/reducer';
import useMediaQuery from '../../../Business/hooks/useMediaQuery';
import { UserMembershipFeatures } from '../../../../enums/user-membership-features';
import { openIntercomChat } from 'utils/openIntercomChat';
import { EntityTypeEnum } from 'types/entity';
import { UserCompanyType } from 'modules/Business/shared/typesTS';
import { UserType } from 'enums/user-type';

const ADD_CLIENTS_TITLE = 'Add a new client to monitor';
const ADD_CLIENTS_SUBTITLE = 'Please if needed add new clients using the field below.';

export const SearchClientAlpha = (props) => {
  const {
    archiveClientsRequest,
    clearAddNewClientWizzardCompaniesList,
    companies,
    hasClientLimitPurchase,
    introducer,
    onClose,
    requestAllClients,
    requestCreateBusinessEntities,
    requestMeData,
    requestSearchCompany,
    userClientsLimit,
    userCompanyId,
    onPreviousClick,
    onSubmitCallback,
    skipDisable,
    infoAlertText,
    showPurchaseCredits,
    title,
  } = props;

  const {
    addedClientsCount,
    archivedCompanies,
    existingCompanies,
    handleOnChange: handleOnChangeSearch,
    handleOnSelect: handleOnSelectCompany,
    isError,
    isLoading,
    isOpen,
    maxMonitorClients,
    onCompanyRemove,
    selectedCompanies,
    setIsError,
    setIsLoading,
    setIsOpen,
    setMaxMonitorClients,
  } = useSearchClientContext();

  const { isMobile } = useMediaQuery();

  useEffect(() => {
    if (userClientsLimit) {
      setMaxMonitorClients(userClientsLimit);
    }
  }, [setMaxMonitorClients, userClientsLimit]);

  const { setRightBarOverlay, closeRightBarOverlay } = useLayout();
  const [companiesToAdd, setCompaniesToAdd] = useState([]);
  const [timer, setTimer] = useState(null);

  const analyticParams = {
    userType: UserType.introducer,
    origin: 'monitor_introducer',
  };

  const entities = companiesToAdd.map((company) => company.registrationNumber);

  const redirectToMonitor = () => {
    setIsLoading(false);
    requestAllClients(userCompanyId);

    clearTimeout(timer);
    if (onSubmitCallback) {
      onSubmitCallback(!!companiesToAdd.length);
      return;
    }

    onClose();
  };

  useSSELoadingMultipleEntities(
    introducer,
    events.ledgerSnapshotGeneration,
    'companyNumber',
    entities,
    redirectToMonitor,
    true,
  );

  const handleOnSelect = (company) => {
    handleOnSelectCompany(userCompanyId, company);
    clearAddNewClientWizzardCompaniesList();
  };

  const mapCompaniesToSearchList = (companiesArr) =>
    companiesArr.map((company) => ({
      label: company.companyName,
      company,
    }));

  const handleOnChange = (value) => {
    if (!value) {
      setIsOpen(false);
      return;
    }

    handleOnChangeSearch();

    requestSearchCompany(value)
      .then(() => setIsLoading(false))
      .catch(() => {
        setIsLoading(false);
        setIsError(true);
      });
  };

  const createPayloadForBusiness = (company) => ({
    name: company.companyName,
    registrationNumber: company.registrationNumber,
    type: EntityTypeEnum.BUSINESS,
    business: {
      type:
        company.type && company.type === 'NonLtd'
          ? UserCompanyType.soleTrader
          : UserCompanyType.limitedCompany,
      contact: {
        name: introducer.get('name'),
        email: introducer.get('email'),
      },
    },
  });

  const handleOnSubmitClients = () => {
    const selectedCompaniesPayloads = selectedCompanies.map((selectedCompany) =>
      createPayloadForBusiness(selectedCompany),
    );
    setIsLoading(true);
    setCompaniesToAdd(selectedCompaniesPayloads);

    const archivedCompaniesIds = archivedCompanies.map((company) => company.entityId);

    if (
      skipDisable &&
      selectedCompaniesPayloads.length === 0 &&
      archivedCompaniesIds.length === 0
    ) {
      redirectToMonitor();
    }

    Analytics.track(AnalyticsEventCategory.monitor, AnalyticsEventAction.addClientSubmit);

    // we will add error handling in a separate ticket
    Promise.allSettled([
      ...requestCreateBusinessEntities(selectedCompaniesPayloads),
      ...archiveClientsRequest(archivedCompaniesIds, userCompanyId),
    ]).finally(() => {
      if (selectedCompaniesPayloads.length === 0) {
        redirectToMonitor();
        return;
      }
      const myTimer = setTimeout(() => {
        redirectToMonitor();
      }, 60000);

      setTimer(myTimer);
    });
  };

  const handlePurchaseCreditsClick = () =>
    setRightBarOverlay(
      <CreditLimitProvider
        maxMonitorClients={maxMonitorClients}
        requestMeData={requestMeData}
        onClose={closeRightBarOverlay}
        totalClientsCount={Number(addedClientsCount) + Number(existingCompanies.length)}
        user={introducer}
      >
        <CreditLimitSidePanel />
      </CreditLimitProvider>,
      {
        size: isMobile ? 'fullWidth' : 'xlarge',
      },
      {
        customZIndex: 10002,
      },
    );

  return (
    <>
      <GridedMain>
        <GridedContent>
          <Box
            gridColumn={{ _: '1/5', sm: '3/7', lg: '5/9' }}
            display="flex"
            flexDirection="column"
            data-testid="SearchClientAlpha"
          >
            {onPreviousClick && (
              <Box display="flex" alignSelf="start" pt={{ _: 'xsmall', md: 'medium' }}>
                <Button
                  baseColor="green"
                  leftIcon={<IconChevronLeft />}
                  onClick={onPreviousClick}
                  size="small"
                  variant="text"
                >
                  Previous
                </Button>
              </Box>
            )}
            <Box marginTop="medium">
              <SearchClientHeaderAlpha title={title} maxClientsCount={maxMonitorClients} />
              <SearchClientTitle
                title={ADD_CLIENTS_TITLE}
                subTitle={ADD_CLIENTS_SUBTITLE}
                clientsCount={addedClientsCount}
              />
              <CompanySearch
                onChange={(company) => handleOnSelect({ company })}
                input={{
                  placeholder: 'e.g. Acme Limited or 70038489',
                  error: isError,
                }}
                analyticParams={analyticParams}
                initAnalytics
              />
              <Box marginTop="3xsmall">
                {infoAlertText && (
                  <Alert
                    icon={IconInfoOutlined}
                    type="info"
                    closable
                    wrapperProps={{ mb: '3xsmall' }}
                  >
                    <Typography size="body-16" color="grey-700">
                      {infoAlertText}
                    </Typography>
                  </Alert>
                )}
                {selectedCompanies?.map((selectedCompany) => (
                  <ListCompanyItem
                    buttonText="Remove"
                    company={selectedCompany}
                    onButtonClick={onCompanyRemove}
                    key={selectedCompany.companyNumber}
                    disabled={isLoading}
                  />
                ))}
              </Box>
              <ArchiveExistingClients disabled={isLoading} />
            </Box>
          </Box>
        </GridedContent>
      </GridedMain>
      <SearchClientFooter
        handleOnClick={handleOnSubmitClients}
        maxMonitorClients={maxMonitorClients}
        handlePurchaseCreditsClick={
          hasClientLimitPurchase ? handlePurchaseCreditsClick : openIntercomChat
        }
        showPurchaseCredits={showPurchaseCredits}
        skipDisable={skipDisable}
      />
    </>
  );
};

SearchClientAlpha.propTypes = {
  archiveClient: PropTypes.func,
  archiveClientsRequest: PropTypes.func,
  clearAddNewClientWizzardCompaniesList: PropTypes.func,
  companies: PropTypes.array,
  entityId: PropTypes.number,
  hasClientLimitPurchase: PropTypes.func,
  introducer: PropTypes.instanceOf(Entity),
  onClose: PropTypes.func.isRequired,
  requestAllClients: PropTypes.func,
  requestCreateBusinessEntities: PropTypes.func,
  requestMeData: PropTypes.func,
  requestSearchCompany: PropTypes.func,
  setMonitorWizzardClient: PropTypes.func,
  userClientsLimit: PropTypes.number,
  userCompanyId: PropTypes.number.isRequired,
  onPreviousClick: PropTypes.func,
  onSubmitCallback: PropTypes.func,
  skipDisable: PropTypes.bool,
  infoAlertText: PropTypes.string,
  showPurchaseCredits: PropTypes.bool,
  title: PropTypes.string,
};

SearchClientAlpha.defaultProps = {
  companies: [],
  skipDisable: false,
  showPurchaseCredits: true,
};

const mapStateToProps = createStructuredSelector({
  companies: makeSelectAddNewClientCompaniesList(),
  hasClientLimitPurchase: makeSelectUserFeature(UserMembershipFeatures.clientLimitPurchase),
  introducer: makeSelectUser(),
  userClientsLimit: makeSelectUserClientsLimit(),
  userCompanyId: makeSelectUserCompanyId(),
});

const mapDispatchToProps = (dispatch) => ({
  archiveClientsRequest: (entityIdsArr, userCompanyId) =>
    entityIdsArr.map((entityId) => dispatch(requestArchiveClient(entityId, userCompanyId))),
  clearAddNewClientWizzardCompaniesList: (client) =>
    dispatch(monitorActions.clearAddNewClientWizzardCompaniesList(client)),
  closeMonitorWizzard: () => dispatch(closeMonitorWizzard()),
  requestAllClients: (entityId) => dispatch(monitorActions.requestAllClients(entityId)),
  requestCreateBusinessEntities: (payloads) =>
    payloads.map((payload) => dispatch(monitorActions.requestCreateBusinessEntities(payload))),
  requestSearchCompany: (info) =>
    new Promise((resolve, reject) =>
      dispatch(monitorActions.requestSearchCompany(info, { resolve, reject })),
    ),
  setMonitorWizzardClient: (client) => dispatch(monitorActions.setMonitorWizzardClient(client)),
  requestMeData: () => dispatch(meRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchClientAlpha);
